import { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { BarChart3, Boxes, CalendarDays, ClipboardList, Cog, CreditCard, Gift, LogOut, Users, LayoutDashboard, Bell, Webhook, ShieldCheck, ListPlus, Menu, X, Droplets, CalendarPlus, Repeat2, RotateCcw, UserRound, ReceiptText, Scale, UsersRound } from "lucide-react";
import { useAuth } from "./AuthContext";
import { Button, buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const nav = [
  { to: "/", label: "Dashboard", Icon: LayoutDashboard, end: true },
  { to: "/bookings", label: "Bookings", Icon: CalendarDays },
  { to: "/queue", label: "Today's queue", Icon: ClipboardList },
  { to: "/waitlists", label: "Waitlists", Icon: ListPlus },
  { to: "/customers", label: "Customers", Icon: Users },
  { to: "/workforce", label: "Workforce", Icon: UsersRound },
  { to: "/inventory", label: "Inventory", Icon: Boxes },
  { to: "/customer-value", label: "Customer value", Icon: Gift },
  { to: "/recurring", label: "Recurring", Icon: Repeat2, roles: ["MANAGER", "ADMIN"] as const },
];

const adminNav = [
  { to: "/refunds", label: "Refunds", Icon: RotateCcw, roles: ["MANAGER", "ADMIN"] as const },
  { to: "/reports", label: "Reports", Icon: BarChart3, roles: ["MANAGER", "ADMIN"] as const },
  { to: "/billing", label: "Billing", Icon: ReceiptText, roles: ["MANAGER", "ADMIN"] as const },
  { to: "/reconciliation", label: "Reconciliation", Icon: Scale, roles: ["MANAGER", "ADMIN"] as const },
  { to: "/payment-operations", label: "Payment operations", Icon: CreditCard, roles: ["MANAGER", "ADMIN"] as const },
  { to: "/notifications", label: "Notifications", Icon: Bell, roles: ["MANAGER", "ADMIN"] as const },
  { to: "/webhooks", label: "Webhooks", Icon: Webhook, roles: ["ADMIN"] as const },
  { to: "/audit", label: "Audit log", Icon: ShieldCheck, roles: ["ADMIN"] as const },
  { to: "/settings", label: "Settings", Icon: Cog, roles: ["ADMIN"] as const },
];

export function AppShell(): JSX.Element {
  const { user, logout, hasRole } = useAuth();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const title = humanize(loc.pathname);
  return (
    <div className="min-h-svh bg-background lg:grid lg:grid-cols-[260px_minmax(0,1fr)]">
      <aside className="hidden border-r border-border/80 bg-white lg:sticky lg:top-0 lg:flex lg:h-svh lg:flex-col">
        <SidebarContent hasRole={hasRole} user={user} logout={logout} />
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button className="absolute inset-0 bg-slate-950/30 backdrop-blur-[2px]" onClick={() => setMobileOpen(false)} aria-label="Close navigation" />
          <aside className="relative flex h-full w-[286px] flex-col bg-white shadow-lift">
            <button className="absolute right-3 top-4 rounded-lg p-2 text-muted-foreground hover:bg-muted" onClick={() => setMobileOpen(false)} aria-label="Close navigation"><X className="h-5 w-5" /></button>
            <SidebarContent hasRole={hasRole} user={user} logout={logout} onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      <main className="flex min-h-svh min-w-0 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border/75 bg-white/90 px-4 backdrop-blur-xl md:px-6 lg:px-8">
          <button className="rounded-lg border bg-white p-2 text-muted-foreground shadow-sm hover:bg-muted lg:hidden" onClick={() => setMobileOpen(true)} aria-label="Open navigation"><Menu className="h-5 w-5" /></button>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold tracking-tight text-foreground">{title}</p>
            <p className="hidden text-xs text-muted-foreground sm:block">Shinebright operations</p>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <Link to="/bookings/new" className={cn(buttonVariants({ size: "sm" }), "hidden sm:inline-flex")}>
              <CalendarPlus className="h-4 w-4" /> New booking
            </Link>
            <div className="grid h-9 w-9 place-items-center rounded-full bg-primary/10 text-xs font-bold text-primary ring-1 ring-primary/10">
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </div>
          </div>
        </header>
        <div className="mx-auto w-full max-w-[1600px] flex-1 p-4 md:p-6 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function SidebarContent({ hasRole, user, logout, onNavigate }: {
  hasRole: (...roles: Array<"ADMIN" | "MANAGER" | "STAFF">) => boolean;
  user: { firstName: string; lastName: string; email: string; role: "ADMIN" | "MANAGER" | "STAFF" } | null;
  logout: () => Promise<void>;
  onNavigate?: () => void;
}) {
  return (
    <>
      <Link to="/" onClick={onNavigate} className="flex h-20 items-center gap-3 border-b border-border/70 px-5">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-primary text-primary-foreground shadow-sm"><Droplets className="h-5 w-5" /></span>
        <span><span className="block text-[17px] font-bold tracking-[-0.03em]">Shinebright</span><span className="block text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">Car care CRM</span></span>
      </Link>
      <nav className="flex flex-1 flex-col overflow-y-auto px-3 py-5">
        <NavSection label="Workspace" items={nav} hasRole={hasRole} onNavigate={onNavigate} />
        <NavSection label="Manage" items={adminNav} hasRole={hasRole} onNavigate={onNavigate} className="mt-6" />
      </nav>
      <div className="m-3 rounded-xl border border-border/80 bg-muted/45 p-3.5">
        <div className="flex items-center gap-3">
          <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-white text-xs font-bold text-primary shadow-sm ring-1 ring-border">{user?.firstName?.[0]}{user?.lastName?.[0]}</div>
          <div className="min-w-0"><p className="truncate text-sm font-semibold">{user?.firstName} {user?.lastName}</p><p className="truncate text-[11px] text-muted-foreground">{user?.email}</p></div>
        </div>
        <div className="mt-3 flex items-center justify-between border-t pt-3">
          <Link to="/account" onClick={onNavigate} className="inline-flex items-center gap-1 text-xs font-medium text-primary"><UserRound className="h-3.5 w-3.5" /> Profile</Link>
          <Button variant="ghost" size="sm" className="h-7 px-2 text-muted-foreground" onClick={() => void logout()}><LogOut className="h-3.5 w-3.5" /> Sign out</Button>
        </div>
      </div>
    </>
  );
}

function NavSection({ label, items, hasRole, onNavigate, className }: {
  label: string; items: typeof nav | typeof adminNav;
  hasRole: (...roles: Array<"ADMIN" | "MANAGER" | "STAFF">) => boolean;
  onNavigate?: () => void; className?: string;
}) {
  return <div className={className}><p className="mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground/75">{label}</p><div className="space-y-1">{items.filter((item) => !item.roles || hasRole(...item.roles)).map(({ to, label: itemLabel, Icon, ...item }) => <NavLink key={to} to={to} end={"end" in item ? item.end : undefined} onClick={onNavigate} className={({ isActive }) => cn("group flex items-center gap-3 rounded-lg px-3 py-2.5 text-[13px] font-medium transition-all", isActive ? "bg-primary text-white shadow-sm" : "text-muted-foreground hover:bg-accent hover:text-accent-foreground")}><Icon className="h-[17px] w-[17px]" />{itemLabel}</NavLink>)}</div></div>;
}

function humanize(path: string): string {
  if (path === "/") return "Dashboard";
  const part = path.split("/").filter(Boolean)[0] ?? "Dashboard";
  return part.replace(/-/g, " ").replace(/^./, (c) => c.toUpperCase());
}
